import { renderingTaxonomyLabel } from "constants/renderingTaxonomies";
import {
  IReprocessedClaimsFilterOptions,
  ReprocessedClaimSource,
  ReprocessedClaimsView,
  ReprocessedRecoveryStatus,
} from "types/claim";
import { formatCurrency } from "utils/claimAdjustments";

/**
 * CLE-2861 — every label and number format on the Reprocessed Claims tab, in one place so the
 * tests can pin them without rendering the table.
 */

export const EM_DASH = "—";

export const VIEW_OPTIONS: { label: string; value: ReprocessedClaimsView }[] = [
  { label: "All Reprocessed Claims", value: "all" },
  { label: "Clear Health Identified", value: "clear_health_identified" },
  { label: "Historical Reprocessing", value: "historical" },
];

export const RECOVERY_STATUS_LABELS: Record<ReprocessedRecoveryStatus, string> =
  {
    FULLY_RECOVERED: "Fully Recovered",
    PARTIALLY_RECOVERED: "Partially Recovered",
    NOT_RECOVERED: "Not Recovered",
    UNKNOWN: "No Expected Amount",
  };

// UNKNOWN has no severity: it renders in the app's neutral grey, not as a good or bad outcome.
export const RECOVERY_STATUS_SEVERITY: Record<
  ReprocessedRecoveryStatus,
  "success" | "warning" | "danger" | undefined
> = {
  FULLY_RECOVERED: "success",
  PARTIALLY_RECOVERED: "warning",
  NOT_RECOVERED: "danger",
  UNKNOWN: undefined,
};

export const UNKNOWN_RECOVERY_TITLE =
  "The payment went up, but this claim has no expected amount to compare it with.";

export const RECOVERY_STATUS_OPTIONS: {
  label: string;
  value: ReprocessedRecoveryStatus;
}[] = (
  [
    "FULLY_RECOVERED",
    "PARTIALLY_RECOVERED",
    "NOT_RECOVERED",
    "UNKNOWN",
  ] as ReprocessedRecoveryStatus[]
).map((status) => ({ label: RECOVERY_STATUS_LABELS[status], value: status }));

export const SOURCE_LABELS: Record<ReprocessedClaimSource, string> = {
  CLEAR_HEALTH_IDENTIFIED: "Clear Health Identified",
  HISTORICAL: "Historical Reprocessing",
};

// A null original payment date means the original adjudication pre-dates the remits on file.
export const BEFORE_FIRST_REMIT_LABEL = "Before first remit";
export const BEFORE_FIRST_REMIT_TITLE =
  "The original payment is not in the uploaded remits; its amount comes from the payer's reversal.";

/**
 * A DATE column's "YYYY-MM-DD" → "MM/DD/YYYY", read straight from the string. `new Date("2026-06-03")`
 * is UTC midnight, which is still June 2 anywhere west of UTC.
 */
export function formatDateOnly(value: string | null | undefined): string {
  if (!value) {
    return EM_DASH;
  }
  const [year, month, day] = value.slice(0, 10).split("-");
  if (!year || !month || !day) {
    return value;
  }
  return `${month}/${day}/${year}`;
}

export type AmountTone = "positive" | "negative" | "neutral";

export function amountTone(value: number | null | undefined): AmountTone {
  if (value === null || value === undefined || value === 0) {
    return "neutral";
  }
  return value > 0 ? "positive" : "negative";
}

/** Cents → "+$250.00" / "-$50.00" / "$0.00". Intl already supplies the minus sign. */
export function formatSignedCurrency(amountInCents: number): string {
  // `0` rather than the value itself, so a -0 never renders as "-$0.00".
  if (amountInCents === 0) {
    return formatCurrency(0);
  }
  const formatted = formatCurrency(amountInCents);
  return amountInCents > 0 ? `+${formatted}` : formatted;
}

/** One decimal, thousands-separated: 12.5 → "12.5%", null → "—". */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return EM_DASH;
  }
  return `${value.toLocaleString("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

/** Like formatPercent, with a "+" on an increase: 12.5 → "+12.5%", -3 → "-3.0%". */
export function formatSignedPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return EM_DASH;
  }
  if (value === 0) {
    return formatPercent(0);
  }
  const formatted = formatPercent(value);
  return value > 0 ? `+${formatted}` : formatted;
}

export const formatCptCodes = (codes: string[]): string => codes.join(", ");

/**
 * The original adjudication's denial classification, as the Claims list's Denial Classification
 * column shows one: the title, falling back to the code. "—" when there is no classification.
 */
export function formatOriginalClassification(
  code: string | null | undefined,
  title: string | null | undefined
): string {
  if (!code) {
    return EM_DASH;
  }
  return title || code;
}

export interface IFilterOption {
  label: string;
  value: string;
}

// The Original Classification filter's value for "rows with no original classification".
export const NO_CLASSIFICATION_VALUE = "none";
export const NO_CLASSIFICATION_LABEL = "No classification";

/**
 * The filter options below come from GET /claims/reprocessed/filter-options and keep the server's
 * order. A missing response (loading, or the request failed) yields no options.
 */

/** "B10 — Code Replacement / Downgrade" (the code alone without a title), then "No classification". */
export function classificationFilterOptions(
  options: IReprocessedClaimsFilterOptions | undefined
): IFilterOption[] {
  if (!options) {
    return [];
  }
  const classified = options.classifications.map((item) => ({
    label: item.title ? `${item.code} — ${item.title}` : item.code,
    value: item.code,
  }));
  return options.hasUnclassified
    ? [
        ...classified,
        { label: NO_CLASSIFICATION_LABEL, value: NO_CLASSIFICATION_VALUE },
      ]
    : classified;
}

/** The taxonomy's name as the Claims list's Taxonomy column shows it; an unknown code stays raw. */
export function renderingTaxonomyFilterOptions(
  options: IReprocessedClaimsFilterOptions | undefined
): IFilterOption[] {
  return (options?.renderingTaxonomies || []).map((item) => ({
    label: renderingTaxonomyLabel(item.code),
    value: item.code,
  }));
}

/** "Jane Smith (1234567890)", or the NPI alone when the name is unknown. */
export function renderingProviderFilterOptions(
  options: IReprocessedClaimsFilterOptions | undefined
): IFilterOption[] {
  return (options?.renderingProviders || []).map((item) => ({
    label: item.name ? `${item.name} (${item.npi})` : item.npi,
    value: item.npi,
  }));
}
